import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  MessageCircle,
  Menu,
  X,
  Mail,
  Send,
  MapPin,
  Clock,
  CheckCircle2,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/motion/Reveal";
import logoAsset from "@/assets/flashweb-logo.webp.asset.json";

const WA_URL =
  "https://wa.me/41782369200?text=Ciao%20Filippo%2C%0A%0AVorrei%20parlarti%20del%20mio%20progetto.%0A%0ANome%20attivit%C3%A0%3A%0ASettore%3A%0ATelefono%3A%0A%0AAttendo%20il%20tuo%20contatto.";

const EMAIL = "info@flash-web.ch";

export const Route = createFileRoute("/contatti")({
  head: () => ({
    meta: [
      { title: "Contatti — FlashWeb Agency | Zug & Ticino" },
      {
        name: "description",
        content:
          "Scrivici su WhatsApp, via email o vieni a trovarci. Sedi a Zug e Bedano. Risposta garantita entro 24 ore lavorative.",
      },
      { property: "og:title", content: "Contatti — FlashWeb Agency" },
      {
        property: "og:description",
        content:
          "WhatsApp, email o form di contatto. Sedi a Zug e Bedano, risposta entro 24 ore lavorative.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.flash-web.ch/contatti" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/contatti" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.flash-web.ch/" },
            { "@type": "ListItem", position: 2, name: "Contatti", item: "https://www.flash-web.ch/contatti" },
          ],
        }),
      },
    ],
  }),
  component: ContattiPage,
});

// ─── Navbar / Footer ───
function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/50 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 group">
          <img
            src={logoAsset.url}
            alt="FlashWeb Agency"
            width="144"
            height="36"
            className="h-9 w-auto brightness-110 contrast-125 transition-transform group-hover:scale-105"
          />
          <span className="font-display text-lg font-bold tracking-tight">FlashWeb</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="hero" size="default" asChild className="hidden sm:inline-flex fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              WhatsApp <ArrowRight />
            </a>
          </Button>
          <Button variant="hero" size="sm" asChild className="sm:hidden fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              WhatsApp <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <button
            type="button"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/60 text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/50 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-3 text-sm">
            {[
              { href: "/", label: "Home" },
              { href: "/servizi", label: "Servizi" },
              { href: "/prezzi", label: "Prezzi" },
              { href: "/come-funziona", label: "Come funziona" },
              { href: "/portfolio", label: "Portfolio" },
              { href: "/recensioni", label: "Recensioni" },
              { href: "/trasparenza", label: "Trasparenza" },
              { href: "/chi-siamo", label: "Chi siamo" },
              { href: "/faq", label: "FAQ" },
              { href: "/blog", label: "Blog" },
              { href: "/contatti", label: "Contatti" },
              { href: "/portal", label: "Area clienti" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <img src={logoAsset.url} alt="FlashWeb Agency" width="112" height="28" className="h-7 w-auto" />
              <span className="font-display text-sm font-bold">FlashWeb Agency</span>
            </div>
            <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} — Tutti i diritti riservati.</p>
            <p className="text-sm text-muted-foreground">
              Un brand di <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sedi</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><span className="font-semibold text-foreground">Bareerstrasse 75</span><br />6300 Zug, Svizzera</p>
              <p><span className="font-semibold text-foreground">Via Ai Prati 2</span><br />Bedano, Svizzera</p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contatti</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><a href={`mailto:${EMAIL}`} className="font-semibold text-foreground hover:underline">{EMAIL}</a></p>
              <p><a href="https://wa.me/41782369200" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:underline">WhatsApp +41 78 236 92 00</a></p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Informazioni legali</h4>
            <p className="text-sm text-muted-foreground">N° registro commercio:<br /><span className="font-semibold text-foreground">CH-170.4.022.832-9</span></p>
            <div className="flex flex-col gap-1.5 pt-1 text-sm">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">Cookie Policy</Link>
              <Link to="/termini-condizioni" className="text-muted-foreground hover:text-foreground">Termini e Condizioni</Link>
              <Link to="/impressum" className="text-muted-foreground hover:text-foreground">Impressum</Link>
            </div>
          </div>
        </div>
        <nav aria-label="Naviga" className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 pt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <Link to="/servizi" className="hover:text-foreground">Servizi</Link>
          <Link to="/prezzi" className="hover:text-foreground">Prezzi</Link>
          <Link to="/come-funziona" className="hover:text-foreground">Come funziona</Link>
          <Link to="/portfolio" className="hover:text-foreground">Portfolio</Link>
          <Link to="/recensioni" className="hover:text-foreground">Recensioni</Link>
          <Link to="/trasparenza" className="hover:text-foreground">Trasparenza</Link>
          <Link to="/chi-siamo" className="hover:text-foreground">Chi siamo</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <Link to="/contatti" className="hover:text-foreground">Contatti</Link>
        </nav>
        <div className="mt-6 flex flex-col gap-2 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} FVT FuturaVision Technology GmbH — Tutti i diritti riservati.</p>
          <p>Realizzato con precisione svizzera 🇨🇭</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Contact form ───
type FormState = {
  name: string;
  company: string;
  sector: string;
  contact: string;
  message: string;
  privacy: boolean;
};

function ContactForm() {
  const [form, setForm] = useState<FormState>({
    name: "",
    company: "",
    sector: "",
    contact: "",
    message: "",
    privacy: false,
  });
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const name = form.name.trim();
    const company = form.company.trim();
    const sector = form.sector.trim();
    const contact = form.contact.trim();
    const message = form.message.trim();

    if (!name || name.length > 100) return setError("Inserisci un nome valido (max 100 caratteri).");
    if (company.length > 120) return setError("Nome azienda troppo lungo (max 120 caratteri).");
    if (sector.length > 80) return setError("Settore troppo lungo (max 80 caratteri).");
    if (!contact || contact.length > 200) return setError("Inserisci email o telefono (max 200 caratteri).");
    if (!message || message.length > 2000)
      return setError("Il messaggio è obbligatorio (max 2000 caratteri).");
    if (!form.privacy) return setError("Devi accettare la privacy policy per continuare.");

    const subject = `Contatto dal sito — ${name}${company ? ` (${company})` : ""}`;
    const body =
      `Nome: ${name}\n` +
      `Azienda: ${company || "—"}\n` +
      `Settore: ${sector || "—"}\n` +
      `Email/Telefono: ${contact}\n\n` +
      `Messaggio:\n${message}`;

    const href = `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  }

  const inputClass =
    "h-11 w-full rounded-xl border border-border bg-background/60 px-4 text-sm text-foreground placeholder:text-muted-foreground transition-colors focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Nome *
          </label>
          <input
            id="name"
            type="text"
            required
            maxLength={100}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className={inputClass}
            placeholder="Mario Rossi"
          />
        </div>
        <div>
          <label htmlFor="company" className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Azienda
          </label>
          <input
            id="company"
            type="text"
            maxLength={120}
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            className={inputClass}
            placeholder="Rossi Studio SA"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="sector" className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Settore
          </label>
          <input
            id="sector"
            type="text"
            maxLength={80}
            value={form.sector}
            onChange={(e) => update("sector", e.target.value)}
            className={inputClass}
            placeholder="Studio legale, ristorazione…"
          />
        </div>
        <div>
          <label htmlFor="contact" className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Email o telefono *
          </label>
          <input
            id="contact"
            type="text"
            required
            maxLength={200}
            value={form.contact}
            onChange={(e) => update("contact", e.target.value)}
            className={inputClass}
            placeholder="nome@azienda.ch"
          />
        </div>
      </div>

      <div>
        <label htmlFor="message" className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Il tuo progetto *
        </label>
        <textarea
          id="message"
          required
          maxLength={2000}
          rows={6}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          className="w-full rounded-xl border border-border bg-background/60 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground transition-colors focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/30"
          placeholder="Raccontaci in poche righe cosa serve: settore, obiettivo, tempi ideali. Rispondiamo con un piano concreto."
        />
        <p className="mt-1.5 text-right text-xs text-muted-foreground">{form.message.length}/2000</p>
      </div>

      <label className="flex items-start gap-3 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={form.privacy}
          onChange={(e) => update("privacy", e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-border bg-background text-primary focus:ring-2 focus:ring-primary/40"
          required
        />
        <span>
          Ho letto la{" "}
          <Link to="/privacy-policy" className="text-primary hover:underline">
            Privacy Policy
          </Link>{" "}
          e acconsento al trattamento dei dati per rispondere alla mia richiesta.
        </span>
      </label>

      {error && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <Button type="submit" variant="hero" size="lg" className="w-full fw-shimmer">
        Invia messaggio <Send className="h-4 w-4" />
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Aprirà il tuo client email con il messaggio precompilato — o scrivici direttamente a{" "}
        <a href={`mailto:${EMAIL}`} className="text-foreground hover:underline">
          {EMAIL}
        </a>
      </p>
    </form>
  );
}

// ═══════════════ PAGE ═══════════════
function ContattiPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0 opacity-60" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-3xl px-6 py-20 text-center sm:py-24">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              Contatti
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl md:text-6xl">
              Parliamo del <span className="text-gradient">tuo sito.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              Rispondiamo entro 24 ore lavorative. Niente centralino, niente ticket
              anonimi — parli sempre con una persona.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THREE CHANNELS */}
      <section className="relative mx-auto w-full max-w-6xl px-6 py-16 sm:py-20">
        <div className="grid gap-5 md:grid-cols-3">
          <Reveal>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex h-full flex-col rounded-2xl border border-primary/40 bg-card/60 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/70 hover:shadow-[0_20px_60px_-20px_var(--primary)]"
            >
              <span className="absolute right-4 top-4 rounded-full bg-gradient-to-r from-primary to-fuchsia-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground shadow-[0_10px_30px_-8px_var(--primary)]">
                Più rapido
              </span>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <MessageCircle className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display text-lg font-bold">WhatsApp</h3>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">
                Il canale che usiamo di più. Risposta media in un paio d'ore lavorative,
                spesso molto meno.
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                +41 78 236 92 00 <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.05}>
            <a
              href={`mailto:${EMAIL}`}
              className="group flex h-full flex-col rounded-2xl border border-border bg-card/60 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-[0_20px_60px_-20px_var(--primary)]"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Mail className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display text-lg font-bold">Email</h3>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">
                Preferibile per allegati, offerte formali e comunicazioni B2B che vogliono
                lasciare traccia.
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
                {EMAIL} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.1}>
            <a
              href="#form"
              className="group flex h-full flex-col rounded-2xl border border-border bg-card/60 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-[0_20px_60px_-20px_var(--primary)]"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Send className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display text-lg font-bold">Form di contatto</h3>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">
                Se preferisci un modulo strutturato: campi essenziali, ti risponde Filippo
                di persona.
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
                Vai al form <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
          </Reveal>
        </div>
      </section>

      {/* FORM */}
      <section id="form" className="relative border-y border-border bg-card/40">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto grid w-full max-w-6xl gap-12 px-6 py-20 sm:py-24 lg:grid-cols-[1fr_1.2fr]">
          <Reveal>
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
                Form di contatto
              </div>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Raccontaci il tuo <span className="text-gradient">progetto.</span>
              </h2>
              <p className="mt-4 text-muted-foreground">
                Compila i campi essenziali. Ti risponde direttamente Filippo, di solito
                in giornata.
              </p>

              <ul className="mt-8 space-y-3">
                {[
                  "Nessun call center, nessun account manager",
                  "Rispondiamo entro 24 ore lavorative",
                  "Se non è il progetto giusto per noi, te lo diciamo",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-foreground/90">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-border bg-background/70 p-6 backdrop-blur sm:p-8">
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SEDI */}
      <section className="relative mx-auto w-full max-w-6xl px-6 py-20 sm:py-24">
        <Reveal>
          <div className="mb-12 max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <MapPin className="h-3 w-3" />
              Sedi
            </div>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Due sedi svizzere. <span className="text-gradient">Su appuntamento.</span>
            </h2>
            <p className="mt-4 text-sm text-muted-foreground sm:text-base">
              Le nostre sedi sono operative e legali, ma <span className="text-foreground">non riceviamo walk-in</span>:
              la maggior parte del lavoro avviene in remoto o in videocall. Per un incontro
              di persona, fissa un appuntamento su WhatsApp.
            </p>
          </div>
        </Reveal>

        <div className="grid gap-6 md:grid-cols-2">
          {[
            {
              city: "Zug",
              address: "Bareerstrasse 75",
              zip: "6300 Zug",
              type: "Sede legale · Svizzera tedesca",
              map: "https://www.google.com/maps?q=Bareerstrasse+75,+6300+Zug,+Svizzera&output=embed",
            },
            {
              city: "Bedano",
              address: "Via Ai Prati 2",
              zip: "6930 Bedano",
              type: "Sede operativa · Ticino",
              map: "https://www.google.com/maps?q=Via+Ai+Prati+2,+Bedano,+Svizzera&output=embed",
            },
          ].map((s, i) => (
            <Reveal key={s.city} delay={i * 0.08}>
              <div className="overflow-hidden rounded-3xl border border-border bg-card/60 backdrop-blur transition-colors hover:border-primary/40">
                <div className="aspect-[16/10] w-full overflow-hidden border-b border-border/60 bg-background">
                  <iframe
                    src={s.map}
                    width="100%"
                    height="100%"
                    style={{ border: 0, filter: "grayscale(0.4) contrast(1.05)" }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`Mappa sede ${s.city}`}
                    className="h-full w-full"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary">
                      <Building2 className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold">{s.city}</h3>
                      <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                        {s.type}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-0.5 text-sm text-foreground/90">
                    <p>{s.address}</p>
                    <p className="text-muted-foreground">{s.zip}, Svizzera 🇨🇭</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ORARI */}
      <section className="relative border-y border-border bg-card/40">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto w-full max-w-4xl px-6 py-16 sm:py-20">
          <div className="grid gap-8 sm:grid-cols-2">
            <Reveal>
              <div className="rounded-3xl border border-border bg-background/70 p-6 backdrop-blur sm:p-8">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary">
                    <Clock className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-lg font-bold">Orari</h3>
                </div>
                <dl className="mt-6 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Lunedì – Venerdì</dt>
                    <dd className="font-semibold text-foreground">09:00 – 18:00 CET</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Sabato</dt>
                    <dd className="font-semibold text-foreground">Solo urgenze</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Domenica</dt>
                    <dd className="font-semibold text-muted-foreground">Chiuso</dd>
                  </div>
                </dl>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-border bg-background/70 p-6 backdrop-blur sm:p-8">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary">
                    <MessageCircle className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-lg font-bold">Tempi di risposta</h3>
                </div>
                <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
                  Prima risposta al contatto{" "}
                  <span className="font-semibold text-foreground">entro 24 ore lavorative</span>,
                  di solito molto meno su WhatsApp.
                </p>
                <p className="mt-3 text-xs text-muted-foreground">
                  Nota: le <span className="text-foreground">48 ore</span> menzionate in home
                  si riferiscono alla consegna del sito una volta ricevuto il briefing
                  completo, non al tempo di prima risposta.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        <div className="mx-auto max-w-3xl px-6 py-20 text-center sm:py-24">
          <Reveal>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Pronto quando <span className="text-gradient">lo sei tu.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Scrivici sul canale che preferisci. Se non hai fretta, guarda prima come
              lavoriamo o cosa include ogni piano.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" asChild className="fw-shimmer">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-5 w-5" /> Scrivici su WhatsApp
                </a>
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <a href="#form">
                  Usa il form <ArrowRight />
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
